import { supabase } from '@/lib/supabase';

/**
 * Слой доступа к данным.
 *
 * Отдельного бэкенда пока нет, поэтому фронтенд обращается к PostgREST Supabase
 * напрямую. Права при этом никуда не деваются: каждый запрос уходит с JWT
 * пользователя, а видимость строк определяют политики RLS из миграции 0013.
 * Когда появится Fastify API, замена коснётся только этого файла.
 */

export type SchemaName = 'core' | 'billing' | 'infrastructure' | 'dispatch' | 'integration' | 'reporting';

/** Минимальный контракт фильтрующего построителя PostgREST, который нам нужен. */
export interface PostgrestFilterLike {
  eq: (column: string, value: unknown) => PostgrestFilterLike;
  neq: (column: string, value: unknown) => PostgrestFilterLike;
  gt: (column: string, value: unknown) => PostgrestFilterLike;
  lt: (column: string, value: unknown) => PostgrestFilterLike;
  gte: (column: string, value: unknown) => PostgrestFilterLike;
  lte: (column: string, value: unknown) => PostgrestFilterLike;
  is: (column: string, value: unknown) => PostgrestFilterLike;
  in: (column: string, values: readonly unknown[]) => PostgrestFilterLike;
  ilike: (column: string, pattern: string) => PostgrestFilterLike;
  like: (column: string, pattern: string) => PostgrestFilterLike;
  or: (filters: string) => PostgrestFilterLike;
}

function client() {
  if (!supabase) {
    throw new Error(
      'Supabase не настроен: задайте VITE_SUPABASE_URL и VITE_SUPABASE_ANON_KEY в apps/web/.env',
    );
  }
  return supabase;
}

/** Таблица в схеме. Схема должна быть добавлена в Settings → API → Exposed schemas. */
export function table(schema: SchemaName, name: string) {
  return client().schema(schema).from(name);
}

/** Вызов хранимой функции. */
export async function rpc<T>(
  schema: SchemaName,
  fn: string,
  args: Record<string, unknown> = {},
): Promise<T> {
  const { data, error } = await client().schema(schema).rpc(fn, args);
  if (error) throw new Error(error.message);
  return data as T;
}

export interface PageParams {
  page: number;
  pageSize: number;
  search?: string;
  orderBy?: string;
  ascending?: boolean;
  /**
   * Способ подсчёта общего числа строк.
   *
   * exact считает всю выборку целиком на каждой странице — на журнале показаний
   * или начислениях это отдельный проход по сотням тысяч строк ради одной цифры
   * в пагинаторе. estimated берёт оценку планировщика, когда строк много,
   * и переходит на точный подсчёт на малых выборках, поэтому справочники
   * по-прежнему показывают точное число.
   */
  count?: 'exact' | 'planned' | 'estimated';
}

export interface Page<T> {
  data: T[];
  total: number;
}

/**
 * Серверная пагинация. Запрошенный count заставляет PostgREST вернуть общее
 * число строк в заголовке Content-Range — без него невозможно нарисовать
 * пагинатор. По умолчанию estimated: см. PageParams.count.
 */
export async function selectPage<T>(
  schema: SchemaName,
  name: string,
  columns: string,
  params: PageParams,
  // Фильтры приходят уже после .select(), поэтому тип берём максимально общий:
  // PostgrestQueryBuilder до вызова select() методов eq/ilike ещё не имеет.
  refine?: (q: PostgrestFilterLike) => PostgrestFilterLike,
): Promise<Page<T>> {
  const from = (params.page - 1) * params.pageSize;
  const to = from + params.pageSize - 1;

  let query = client()
    .schema(schema)
    .from(name)
    .select(columns, { count: params.count ?? 'estimated' })
    .range(from, to);

  if (params.orderBy) {
    query = query.order(params.orderBy, { ascending: params.ascending ?? true });
  }
  if (refine) {
    query = refine(query as unknown as PostgrestFilterLike) as unknown as typeof query;
  }

  const { data, error, count } = await query;
  if (error) throw new Error(error.message);

  return { data: (data ?? []) as T[], total: count ?? 0 };
}

/** Количество строк без выборки самих данных. */
export async function countRows(
  schema: SchemaName,
  name: string,
  count: 'exact' | 'planned' | 'estimated' = 'estimated',
): Promise<number> {
  const { count: total, error } = await client()
    .schema(schema)
    .from(name)
    .select('*', { count, head: true });
  if (error) throw new Error(error.message);
  return total ?? 0;
}
